import { JourneyPhase } from '@/hooks/useJourneyProgress';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowRight, Heart, Target, Sparkles, Map, CheckCircle2, Users, Lock, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

// Time estimates for completing each prerequisite phase
const PHASE_TIME_ESTIMATES: Record<string, string> = {
  values: '5-10 minutes',
};

// Benefit copy explaining why completing the prerequisite unlocks better results
const PHASE_BENEFIT_COPY: Record<string, Record<string, string>> = {
  values: {
    goals: 'Discover your core values first so you can set goals that actually matter to you.',
    habits: 'Build habits aligned with your values so you can grow in ways that matter most.',
    plan: 'Discover your core values first so your development plan reflects what matters to you.',
  },
};

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Heart,
  Target,
  Sparkles,
  Map,
  CheckCircle2,
  Users,
};

interface PhaseContextBannerProps {
  currentPhase: JourneyPhase;
  phases: JourneyPhase[];
  activeTab: string;
  onNavigate: (tabValue: string) => void;
}

export const PhaseContextBanner = ({
  currentPhase,
  phases,
  activeTab,
  onNavigate,
}: PhaseContextBannerProps) => {
  const activePhase = phases.find(p => p.tabValue === activeTab);
  if (!activePhase) return null;

  // Check if user is viewing a phase ahead of their current progress
  const activePhaseIndex = phases.findIndex(p => p.id === activePhase.id);
  const currentPhaseIndex = phases.findIndex(p => p.id === currentPhase.id);
  const isAheadOfProgress = activePhaseIndex > currentPhaseIndex && !activePhase.isComplete;

  // Check for prerequisite phases
  const prerequisitePhases = phases
    .slice(0, activePhaseIndex)
    .filter(p => p.requiredForNext && !p.isComplete);

  const hasUnmetPrereqs = prerequisitePhases.length > 0;

  // Find the next incomplete phase after the current tab's phase
  const nextPhaseIndex = phases.findIndex((p, i) => i > activePhaseIndex && !p.isComplete);
  const nextPhase = nextPhaseIndex >= 0 ? phases[nextPhaseIndex] : null;

  // If viewing current or past phase and it's complete, show next step
  if (activePhase.isComplete && nextPhase) {
    const NextIcon = iconMap[nextPhase.icon];
    return (
      <Alert className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20 pointer-events-none">
        <CheckCircle2 className="h-4 w-4 text-primary" />
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>
            <strong>{activePhase.name}</strong> complete! Continue your journey with{' '}
            <strong>{nextPhase.name}</strong>.
          </span>
          <Button size="sm" variant="default" className="gap-2 shrink-0 pointer-events-auto" onClick={() => onNavigate(nextPhase.tabValue)}>
            <NextIcon className="w-4 h-4" />
            {nextPhase.shortName}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // If ahead of progress with unmet prerequisites
  if (hasUnmetPrereqs) {
    const firstPrereq = prerequisitePhases[0];
    const PrereqIcon = iconMap[firstPrereq.icon];
    const timeEstimate = PHASE_TIME_ESTIMATES[firstPrereq.id];
    const benefitCopy = PHASE_BENEFIT_COPY[firstPrereq.id]?.[activePhase.tabValue];
    return (
      <Alert className="bg-amber-500/10 border-amber-500/30 pointer-events-none">
        <Lock className="h-4 w-4 text-amber-500" />
        <AlertDescription className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="space-y-1">
            <span className="block">
              {benefitCopy || (
                <>Complete <strong>{firstPrereq.name}</strong> first to unlock the full power of{' '}
                <strong>{activePhase.name}</strong>.</>
              )}
            </span>
            {timeEstimate && (
              <span className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                <Clock className="w-3 h-3" />
                Takes about {timeEstimate}
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 shrink-0 border-amber-500/50 hover:bg-amber-500/10 pointer-events-auto"
            onClick={() => onNavigate(firstPrereq.tabValue)}
          >
            <PrereqIcon className="w-4 h-4" />
            Go to {firstPrereq.shortName}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // If viewing current incomplete phase, no banner needed — the tab content
  // itself communicates progress (e.g. "0/3 values discovered").

  return null;
};
